/**
 * Main Application Class for Astrophotography Planning Calculator
 * 
 * This class orchestrates all components and provides the main interface
 * for the JavaScript implementation of the astronomical imaging calculator.
 */

import AstroImagingCalculator from './astroCalculator.js';
import FastAstroSearch from './searchEngine.js';
import { PlotlyManager } from './plotlyManager.js';
import { SimbadClient } from './simbadClient.js';
import { formatLatitude, formatLongitude } from './coordinateFormat.js';
// APIClient removed for browser-only version

export class AstroImagingApp {
    constructor() {
        this.calculator = new AstroImagingCalculator();
        this.searchEngine = new FastAstroSearch();
        this.plotlyManager = new PlotlyManager();
        this.simbadClient = new SimbadClient();
        // APIClient removed for browser-only version
        this.isInitialized = false;
        this.currentData = null;
        this.currentParams = null;
    }

    /**
     * Initialize the application
     * @param {{onDataReload?: Function}} [options] - onDataReload is called when the
     *   object catalogue has to be (re)loaded, which takes a while
     * @returns {Promise<boolean>} Success status
     */
    async initialize({ onDataReload } = {}) {
        if (this.isInitialized) {
            return true;
        }

        try {
            console.log('Initializing Astrophotography Planning Calculator...');
            
            // Initialize search engine
            await this.searchEngine.initialize();
            
            // Initialize plotly manager
            this.plotlyManager.initialize();
            
            // Load the object database from the published JSON when the cache is empty or outdated
            try {
                await this.searchEngine.ensureDataLoaded({ onReloadStart: onDataReload });
            } catch (error) {
                console.error('Failed to load data from JSON files:', error);
            }
            
            // FIXED: Validate calculations on startup
            console.log('Validating astronomical calculations...');
            const validationResults = this.calculator.validateCalculations();
            if (!Object.values(validationResults).every(result => result === true)) {
                console.warn('Some calculations failed validation - results may be inaccurate');
            }
            
            this.isInitialized = true;
            console.log('Application initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize application:', error);
            return false;
        }
    }

    /**
     * Calculate imaging hours for an object
     * @param {Object} params - Calculation parameters
     * @returns {Promise<Object>} Calculation results
     */
    async calculateImagingHours(params) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            let objectCoords;
            const planetName = params.planet && params.planet.name ? params.planet.name : null;
            const cometDesignation = params.comet && params.comet.designation ? params.comet.designation : null;

            // Check if manual coordinates are provided
            if (params.manual_coords) {
                objectCoords = {
                    ra_hours: params.manual_coords.ra_hours,
                    dec_degrees: params.manual_coords.dec_degrees,
                    name: params.manual_coords.name
                };
                console.log('Using manual coordinates:', objectCoords);
            } else if (planetName) {
                // Planet target uses dynamic coordinates; no database lookup
                console.log('Using planet target:', planetName);
                params.object_name = planetName;
                objectCoords = null;
            } else if (cometDesignation) {
                // Comet target uses dynamic coordinates from JPL Horizons
                console.log('Using comet target:', cometDesignation);
                params.object_name = cometDesignation;
                objectCoords = null;
            } else {
                // First try to get object coordinates from local database
                objectCoords = await this.getObjectCoordinates(params.object_name);
                
                if (!objectCoords) {
                    // Try to find similar objects
                    const suggestions = await this.searchObjects(params.object_name, 5);
                    let errorMessage = `Object '${params.object_name}' not found in local database.`;
                    
                    if (suggestions.length > 0) {
                        const suggestionNames = suggestions.map(s => s.display_name || s.name).slice(0, 3);
                        errorMessage += ` Did you mean: ${suggestionNames.join(', ')}?`;
                    }
                    
                    throw new Error(errorMessage);
                }
            }

            // Parse parameters
            const latitudes = params.latitudes.split(',').map(lat => parseFloat(lat.trim())).filter(v => !isNaN(v));
            const longitudes = params.longitudes.split(',').map(lon => parseFloat(lon.trim())).filter(v => !isNaN(v));
            // Use year from form if provided; fallback to current year
            let year = parseInt(params.year, 10);
            if (!isFinite(year)) {
                year = new Date().getFullYear();
            }
            // Sanity clamp to a reasonable range if needed
            if (year < 1900 || year > 2100) {
                year = new Date().getFullYear();
            }
            // Validate lat/lon pairing
            if (latitudes.length === 0 || longitudes.length === 0 || latitudes.length !== longitudes.length) {
                throw new Error('Latitudes and longitudes must be non-empty and have the same number of entries.');
            }
            const numDatesPerMonth = parseInt(params.samples_per_month);
            const altitudeThreshold = parseFloat(params.altitude_threshold);
            const sunAltThreshold = parseFloat(params.sun_alt_threshold);
            const enableMoonAltFilter = params.enable_moon_alt_filter === true;
            const moonAltThreshold = parseFloat(params.moon_alt_threshold);
            const enableMoonSepFilter = params.enable_moon_sep_filter === true;
            const moonSepThreshold = parseFloat(params.moon_sep_threshold);
            const enableMoonPhaseFilter = params.enable_moon_phase_filter === true;
            const moonPhaseThreshold = parseFloat(params.moon_phase_threshold);
            if (enableMoonPhaseFilter && !(Number.isFinite(moonPhaseThreshold) && moonPhaseThreshold >= 0 && moonPhaseThreshold <= 100)) {
                throw new Error('Moon illumination threshold must be 0–100%');
            }

            console.log('Starting calculations with coordinates:', objectCoords || { planet: planetName, comet: cometDesignation });
            console.log('Parameters:', {latitudes, longitudes, year, numDatesPerMonth, altitudeThreshold, sunAltThreshold, enableMoonAltFilter, moonAltThreshold, enableMoonSepFilter, moonSepThreshold, enableMoonPhaseFilter, moonPhaseThreshold});

            // Calculate theoretical visibility hours
            const results = await this.calculateQualityHours(
                objectCoords ? objectCoords.ra_hours : null,
                objectCoords ? objectCoords.dec_degrees : null,
                planetName ? { kind: 'planet', planetName } : cometDesignation ? { kind: 'comet', cometDesignation } : null,
                latitudes,
                longitudes, // FIXED: Now passing longitudes
                year,
                numDatesPerMonth,
                altitudeThreshold,
                sunAltThreshold,
                enableMoonAltFilter,
                moonAltThreshold,
                enableMoonSepFilter,
                moonSepThreshold,
                enableMoonPhaseFilter,
                moonPhaseThreshold
            );
            
            console.log('Calculation results:', results);

            // Create plot
            const plotData = this.createQualityImagingPlot(results, params.object_name, year, numDatesPerMonth);

            // Calculate summary statistics
            const summary = this.calculateSummaryStats(results);
            console.log('Summary stats:', summary);

            this.currentData = {
                plot: plotData,
                summary: summary,
                results: results
            };
            this.currentParams = params;

            return {
                success: true,
                plot: plotData,
                summary: summary,
                data: results
            };
        } catch (error) {
            console.error('Calculation error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Get object coordinates from local database or backend
     * @param {string} objectName - Object name
     * @returns {Promise<Object|null>} Coordinates or null
     */
    async getObjectCoordinates(objectName) {
        // Try local database first
        const localObject = await this.searchEngine.getObjectByName(objectName);
        if (localObject && localObject.ra_deg !== null && localObject.dec_deg !== null) {
            return {
                ra_hours: localObject.ra_deg / 15.0, // FIXED: Convert degrees to hours correctly
                dec_degrees: localObject.dec_deg,
                source: 'local'
            };
        }

        // Fallback to SIMBAD if not found locally
        console.log(`Object '${objectName}' not found in local database, trying SIMBAD...`);
        try {
            // The robust resolver already tries the basic and script queries for this name
            const simbadObject = await this.simbadClient.getObjectCoordinatesRobust(objectName);

            if (simbadObject) {
                return {
                    ra_hours: simbadObject.ra_hours,
                    dec_degrees: simbadObject.dec_degrees,
                    source: 'SIMBAD',
                    name: simbadObject.name,
                    object_type: simbadObject.object_type
                };
            }

            // Last resort: SIMBAD pattern search, take the best match
            try {
                const matches = await this.simbadClient.searchObjects(objectName, 1);
                if (matches && matches.length > 0) {
                    const m = matches[0];
                    return {
                        ra_hours: m.ra_hours,
                        dec_degrees: m.dec_degrees,
                        source: 'SIMBAD',
                        name: m.name || objectName,
                        object_type: m.object_type || 'Unknown'
                    };
                }
            } catch (e) {
                console.warn('SIMBAD searchObjects fallback failed:', e);
            }
        } catch (error) {
            console.error('SIMBAD fallback failed:', error);
            // Log more details to help debug
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                objectName: objectName
            });
        }

        console.warn(`Object '${objectName}' not found in local database or SIMBAD`);
        return null;
    }

    /**
     * Calculate theoretical visibility hours for multiple latitudes
     * @param {number} raHours - RA in hours
     * @param {number} decDegrees - Dec in degrees
     * @param {Array} latitudes - Array of latitudes
     * @param {Array} longitudes - Array of longitudes (FIXED: now properly used)
     * @param {number} year - Year
     * @param {number} numDatesPerMonth - Number of dates per month
     * @param {number} altitudeThreshold - Altitude threshold
     * @param {number} sunAltThreshold - Sun altitude threshold
     * @param {number} moonAltThreshold - Moon altitude threshold
     * @param {boolean} enableMoonPhaseFilter - Skip Moon filters on dates when the Moon is faint
     * @param {number} moonPhaseThreshold - Illumination % below which the Moon is ignored
     * @returns {Object} Results by latitude
     */
    async calculateQualityHours(raHours, decDegrees, target, latitudes, longitudes, year, numDatesPerMonth, altitudeThreshold, sunAltThreshold, enableMoonAltFilter = false, moonAltThreshold = 0.0, enableMoonSepFilter = false, moonSepThreshold = 30.0, enableMoonPhaseFilter = false, moonPhaseThreshold = 25) {
        const results = {};
        const dateList = this.calculator.generateDateList(year, numDatesPerMonth);
        let moonByDate = null; // Moon phase per date, for the chart (first location's timezone)

        for (let i = 0; i < latitudes.length; i++) {
            const latitude = latitudes[i];
            const longitude = longitudes[i];
            const hours = [];
            // Determine site timezone for this location
            const siteTimezone = this.calculator.getTimezoneForLocation(latitude, longitude);
            if (i === 0) {
                moonByDate = this.calculator.getMoonPhaseSeries(year, dateList, siteTimezone);
            }
            // Note: comet prefetch disabled to avoid API 500s; per-day caching handles efficiency.
            
            for (const { month, day } of dateList) {
                const qualityHours = await this.calculator.computeQualityImagingHoursForDay(
                    raHours,
                    decDegrees,
                    latitude,
                    longitude, // FIXED: Now passing the correct longitude
                    year,
                    month,
                    day,
                    {
                        altitudeThreshold,
                        sunAltThreshold,
                        enableMoonAltFilter,
                        moonAltThreshold,
                        enableMoonSepFilter,
                        moonSepThreshold,
                        enableMoonPhaseFilter,
                        moonPhaseThreshold,
                        timeStepMinutes: (target && target.kind === 'comet') ? 30 : undefined,
                        timezone: siteTimezone,
                        target: target || null
                    }
                );
                hours.push(qualityHours);
            }
            
            results[latitude] = hours;
        }

        return {
            results,
            dateList,
            latitudes,
            moonByDate
        };
    }

    /**
     * Create theoretical visibility hours plot
     * @param {Object} results - Calculation results
     * @param {string} objectName - Object name
     * @param {number} year - Year
     * @param {number} numDatesPerMonth - Number of dates per month
     * @returns {Object} Plotly plot data
     */
    createQualityImagingPlot(results, objectName, year, numDatesPerMonth) {
        const { results: hoursByLat, dateList, latitudes, moonByDate } = results;
        // Use actual date strings on x-axis to avoid numeric headers like "130" in unified hover
        const xVals = dateList.map(({ month, day }) => {
            const mm = month.toString().padStart(2, '0');
            const dd = day.toString().padStart(2, '0');
            return `${year}-${mm}-${dd}`;
        });
        
        const traces = [];
        const colors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b'];

        latitudes.forEach((lat, index) => {
            const hours = hoursByLat[lat];
            
            // Apply 3-point moving average for smoother visualization
            const smoothedHours = hours.map((val, idx) => {
                if (idx === 0 || idx === hours.length - 1) return val; // Keep endpoints
                const prev = hours[idx - 1] || val;
                const next = hours[idx + 1] || val;
                return (prev + val + next) / 3;
            });
            
            // Super-smooth lines (spline) without markers for a cleaner curve
            traces.push({
                x: xVals,
                y: smoothedHours, // Use smoothed values for visual
                mode: 'lines',
                name: `Lat ${formatLatitude(lat)}`,
                line: {
                    color: colors[index % colors.length],
                    width: 3,
                    shape: 'spline',
                    smoothing: 1.8 // Increased smoothing to reduce "teeth" appearance
                },
                hoverinfo: 'x+y+name',
                connectgaps: false, // Don't connect gaps - show actual data
                hovertemplate: `Date: %{text}<br>Hours: %{y:.2f}<br>Raw: %{customdata:.2f}<extra></extra>`,
                text: dateList.map(({ month, day }) => `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`),
                customdata: hours // Store original precise values for hover
            });
        });

        // Create tick positions and labels for day 1 and 15
        const tickPositions = [];
        const tickLabels = [];
        
        dateList.forEach(({ month, day }, index) => {
            if (day === 1 || day === 15) {
                tickPositions.push(xVals[index]);
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                tickLabels.push(`${monthNames[month - 1]} ${day}`);
            }
        });

        // Calculate dynamic height and range (mobile-friendly)
        const onMobile = (typeof window !== 'undefined') && window.innerWidth < 768;
        const allHours = Object.values(hoursByLat).flat();
        const maxHours = Math.max(...allHours);
        const baseHeight = onMobile ? 380 : 500;
        const plotHeight = Math.max(baseHeight, Math.min(onMobile ? 700 : 800, 320 + latitudes.length * 45 + maxHours * 18));

        const layout = {
            title: `Theoretical Visibility Hours for '${objectName}' in ${year}<br>` +
                   `(Ideal conditions: Object altitude >20°, Sun <-15°, excludes weather/atmosphere)`,
            xaxis: {
                title: { text: 'Date (only 1st & 15th labeled)', font: { color: '#b0b0b0' } },
                tickmode: 'array',
                tickvals: tickPositions,
                ticktext: tickLabels,
                tickangle: 45,
                type: 'category', // treat dates as categories so unified hover shows date strings, not indices
                gridcolor: '#2a2a2a',
                linecolor: '#2a2a2a',
                tickfont: { color: '#b0b0b0' }
            },
            yaxis: {
                title: { text: 'Theoretical Visibility Hours (Per Night)', font: { color: '#b0b0b0' } },
                range: [0, Math.max(8, maxHours + 1)],
                gridcolor: '#2a2a2a',
                linecolor: '#2a2a2a',
                tickfont: { color: '#b0b0b0' }
            },
            // Show tooltips for any x-position (unified for all traces)
            hovermode: 'x unified',
            hoverdistance: 20,
            spikedistance: -1,
            paper_bgcolor: '#0a0a0a',
            plot_bgcolor: '#141414',
            showlegend: true,
            legend: {
                orientation: 'h',
                x: 1,
                xanchor: 'right',
                y: 1,
                yanchor: 'top',
                bgcolor: 'rgba(10, 10, 10, 0.9)',
                bordercolor: '#2a2a2a',
                borderwidth: 1,
                font: { size: 10, color: '#b0b0b0' }
            },
            height: plotHeight,
            width: null, // Let Plotly use container width
            autosize: true,
            margin: onMobile ? { l: 48, r: 18, t: 80, b: 60 } : { l: 60, r: 50, t: 100, b: 80 }
        };

        if (moonByDate && moonByDate.length === xVals.length) {
            this.addMoonPhaseStrip(traces, layout, xVals, moonByDate);
        }

        return {
            data: traces,
            layout: layout
        };
    }

    /**
     * Add the Moon phase to the yearly chart: a thin illumination strip under the
     * chart (stacked y2 axis) plus an invisible helper trace so the unified hover
     * shows "Moon: <phase> (<illum>%)" once per date.
     * @param {Array} traces - Plot traces (location traces first; their indices are kept)
     * @param {Object} layout - Plot layout (modified in place)
     * @param {Array<string>} xVals - Category date strings
     * @param {Array<{illumination: number, label: string}>} moonByDate - Phase per date
     */
    addMoonPhaseStrip(traces, layout, xVals, moonByDate) {
        const labels = moonByDate.map((m) => m.label);

        // Hover helper on the main axes (stacked subplots don't join unified hover in Plotly 2.26)
        traces.push({
            x: xVals,
            y: xVals.map(() => 0),
            mode: 'markers',
            marker: { size: 1, opacity: 0 },
            name: 'Moon',
            text: labels,
            hovertemplate: 'Moon: %{text}<extra></extra>',
            showlegend: false
        });

        // Phase strip: dark at new Moon, pale at full Moon
        traces.push({
            type: 'bar',
            x: xVals,
            y: xVals.map(() => 1),
            yaxis: 'y2',
            name: 'Moon phase',
            text: labels,
            textposition: 'none',
            hovertemplate: '%{x}<br>Moon: %{text}<extra></extra>',
            marker: {
                color: moonByDate.map((m) => Math.round(m.illumination * 100)),
                colorscale: [[0, '#1c2030'], [1, '#f3e9c0']],
                cmin: 0,
                cmax: 100,
                line: { width: 0 }
            },
            showlegend: false
        });

        layout.yaxis.domain = [0.11, 1];
        layout.yaxis2 = {
            domain: [0, 0.06],
            range: [0, 1],
            fixedrange: true,
            tickvals: [0.5],
            ticktext: ['Moon'],
            showgrid: false,
            zeroline: false,
            linecolor: '#2a2a2a',
            tickfont: { color: '#b0b0b0', size: 10 }
        };
        layout.xaxis.anchor = 'y2';
        const onMobile = (typeof window !== 'undefined') && window.innerWidth < 768;
        layout.xaxis.title.text = onMobile
            ? 'Date · strip: Moon illumination'
            : 'Date (only 1st & 15th labeled) · strip: Moon illumination';
        layout.bargap = 0;
        layout.height += 30;
    }


    /**
     * Calculate summary statistics
     * @param {Object} results - Calculation results
     * @returns {Object} Summary statistics
     */
    calculateSummaryStats(results) {
        const { results: hoursByLat } = results;
        const allHours = Object.values(hoursByLat).flat();
        
        if (allHours.length === 0) {
            return {
                total_records: 0,
                avg_hours: 0,
                max_hours: 0,
                min_hours: 0
            };
        }

        const sum = allHours.reduce((acc, val) => acc + val, 0);
        const avg = sum / allHours.length;
        const max = Math.max(...allHours);
        const min = Math.min(...allHours);

        return {
            total_records: allHours.length,
            avg_hours: avg,
            max_hours: max,
            min_hours: min
        };
    }

    /**
     * Create altitude plot for a specific date and location
     * @param {Object} params - Altitude plot parameters
     * @returns {Promise<Object>} Altitude plot data
     */
    async createAltitudePlot(params) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            let objectCoords;
            const planetName = params.planet && params.planet.name ? params.planet.name : null;
            const isMoon = this.calculator.isMoonTarget({ kind: 'planet', planetName });
            const cometDesignation = params.comet && params.comet.designation ? params.comet.designation : null;

            // Check if manual coordinates are provided
            if (params.manual_coords) {
                objectCoords = {
                    ra_hours: params.manual_coords.ra_hours,
                    dec_degrees: params.manual_coords.dec_degrees,
                    name: params.manual_coords.name
                };
                console.log('Using manual coordinates for altitude plot:', objectCoords);
            } else if (planetName) {
                console.log('Using planet target for altitude plot:', planetName);
                objectCoords = null;
            } else if (cometDesignation) {
                console.log('Using comet target for altitude plot:', cometDesignation);
                objectCoords = null;
            } else {
                // Get object coordinates from database
                objectCoords = await this.getObjectCoordinates(params.object_name);
                if (!objectCoords) {
                    // Try to find similar objects
                    const suggestions = await this.searchObjects(params.object_name, 5);
                    let errorMessage = `Object '${params.object_name}' not found in local database.`;
                    
                    if (suggestions.length > 0) {
                        const suggestionNames = suggestions.map(s => s.display_name || s.name).slice(0, 3);
                        errorMessage += ` Did you mean: ${suggestionNames.join(', ')}?`;
                    }
                    
                    throw new Error(errorMessage);
                }
            }

            // Parse parameters
            const latitude = parseFloat(params.latitude);
            const longitude = parseFloat(params.longitude);
            let elevation = parseFloat(params.elevation);
            if (!isFinite(elevation)) elevation = 0;
            const timezone = params.timezone;

            // FIXED: Parse the observation date the same way as debug test to avoid timezone issues
            // Use simple date parsing like the debug test that works correctly
            const parsedObsDate = new Date(params.obs_date);
            // Parsed observation date and timezone handled above

            // Validate inputs for Altitude Plot
            if (!isFinite(latitude) || latitude < -90 || latitude > 90) {
                throw new Error('Latitude must be a number between -90 and 90 degrees.');
            }
            if (!isFinite(longitude) || longitude < -180 || longitude > 180) {
                throw new Error('Longitude must be a number between -180 and 180 degrees.');
            }
            if (!(parsedObsDate instanceof Date) || isNaN(parsedObsDate.getTime())) {
                throw new Error('Invalid observation date.');
            }
            
            // Generate time points for the day
            const timePoints = this.generateTimePoints(parsedObsDate, 2); // 2-minute intervals
            
            // Build initial time grid; detailed debug logs removed
            
            // Calculate altitudes for each time point
            const objectAlts = [];
            const moonAlts = [];
            const horizonAlts = [];
            const objectAzimuths = [];
            const moonSeparations = [];
            const times = [];

            // Parse optional custom horizon profile from UI (default: none)
            const parseHorizon = (text) => {
                if (!text || !text.trim()) return null;
                const trimmed = text.trim();
                // Try JSON first
                if (trimmed.startsWith('[')) {
                    try {
                        const arr = JSON.parse(trimmed);
                        const out = [];
                        (arr || []).forEach(pair => {
                            if (Array.isArray(pair) && pair.length >= 2) {
                                const az = parseFloat(pair[0]);
                                const alt = parseFloat(pair[1]);
                                if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                            }
                        });
                        return out.length ? out : null;
                    } catch (_) { /* ignore and try other formats */ }
                }
                // Split by newlines or semicolons
                const entries = trimmed.split(/\n|;+/).map(s => s.trim()).filter(Boolean);
                const out = [];
                for (const e of entries) {
                    const parts = e.split(/[ ,\t]+/).filter(Boolean);
                    if (parts.length >= 2) {
                        const az = parseFloat(parts[0]);
                        const alt = parseFloat(parts[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                        continue;
                    }
                    const nums = e.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
                    if (nums && nums.length >= 2) {
                        const az = parseFloat(nums[0]);
                        const alt = parseFloat(nums[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                    }
                }
                return out.length ? out : null;
            };
            const horizonProfile = parseHorizon(params.horizon_data);

            for (let i = 0; i < timePoints.length; i++) {
                const time = timePoints[i];
                const objectAltAz = planetName
                    ? this.calculator.calculatePlanetAltAz(time, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometAltAz(time, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.raDecToAltAz(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        latitude,
                        longitude,
                        time,
                        elevation
                    );
                
                let moonAlt;
                try {
                    moonAlt = this.calculator.calculateMoonAltitude(time, latitude, longitude, elevation);
                    // FIXED: Handle error return value (-90) by replacing with null for gaps
                    if (moonAlt === -90) {
                        moonAlt = null; // This will create a gap in the plot instead of a line to -90
                    }
                    
                    // Per-point debug logging removed
                } catch (error) {
                    // Per-point warning removed to avoid noisy logs
                    moonAlt = null; // Create gap instead of error value
                }

                // Calculate angular separation between object and Moon
                let moonSeparation = null;
                try {
                    moonSeparation = planetName
                        ? this.calculator.calculatePlanetMoonSeparation(time, latitude, longitude, elevation, planetName)
                        : cometDesignation
                        ? await this.calculator.calculateCometMoonSeparation(time, latitude, longitude, elevation, cometDesignation)
                        : this.calculator.calculateObjectMoonSeparation(
                            objectCoords.ra_hours,
                            objectCoords.dec_degrees,
                            time,
                            latitude,
                            longitude,
                            elevation
                        );
                } catch (error) { /* suppress noisy per-point warnings */ }

                // Interpolate horizon altitude for this azimuth (only if profile provided)
                let horizonAlt = null;
                if (horizonProfile) {
                    horizonAlt = this.calculator.interpolateHorizonAltitude(objectAltAz.azimuth, horizonProfile);
                }
                
                // FIXED: Add validation for calculated values
                if (isNaN(moonAlt) && moonAlt !== null) {
                    console.warn('Invalid moon altitude calculated for time:', time, 'moonAlt:', moonAlt);
                    moonAlt = null; // Convert NaN to null for proper gap handling
                }
                if (isNaN(objectAltAz.altitude)) {
                    console.warn('Invalid object altitude calculated for time:', time, 'objAlt:', objectAltAz.altitude);
                }
                
                objectAlts.push(objectAltAz.altitude);
                moonAlts.push(moonAlt);
                horizonAlts.push(horizonAlt);
                objectAzimuths.push(objectAltAz.azimuth);
                moonSeparations.push(moonSeparation);
                times.push(time);
            }

            // FIXED: Debug calculated values (filter out null values for range calculation)
            console.log('Altitude calculation summary:');
            console.log('Object altitudes range:', Math.min(...objectAlts), 'to', Math.max(...objectAlts));
            console.log('Times array length:', times.length);
            console.log('First 5 times:', times.slice(0, 5).map(t => t.toLocaleString()));
            console.log('Last 5 times:', times.slice(-5).map(t => t.toLocaleString()));
            
            const validMoonAlts = moonAlts.filter(val => val !== null && val !== -90 && !isNaN(val));
            if (validMoonAlts.length > 0) {
                console.log('Moon altitudes range:', Math.min(...validMoonAlts), 'to', Math.max(...validMoonAlts));
            } else {
                console.log('Moon altitudes: No valid values found');
            }
            
            if (horizonProfile) {
                const validH = horizonAlts.filter(v => v !== null && !isNaN(v));
                if (validH.length > 0) {
                    console.log('Horizon altitudes range:', Math.min(...validH), 'to', Math.max(...validH));
                }
            }
            console.log('Total time points:', times.length);
            
            // Check for any NaN values and null values
            const nanObjectAlts = objectAlts.filter(isNaN).length;
            const nanMoonAlts = moonAlts.filter(val => isNaN(val) && val !== null).length;
            const nullMoonAlts = moonAlts.filter(val => val === null).length;
            const errorMoonAlts = moonAlts.filter(val => val === -90).length;
            const nanHorizonAlts = horizonAlts.filter(isNaN).length;
            
            console.log('Moon altitude analysis:');
            console.log('- Valid values:', moonAlts.filter(val => val !== null && val !== -90 && !isNaN(val)).length);
            console.log('- Null values (gaps):', nullMoonAlts);
            console.log('- Error values (-90):', errorMoonAlts);
            console.log('- NaN values:', nanMoonAlts);
            
            if (nanObjectAlts > 0 || nanMoonAlts > 0 || nanHorizonAlts > 0) {
                console.warn('Found NaN values:', {nanObjectAlts, nanMoonAlts, nanHorizonAlts});
            }
            
            if (nullMoonAlts > 0 || errorMoonAlts > 0) {
                console.warn('Moon calculation issues:', {nullMoonAlts, errorMoonAlts});
            }

            // Calculate Sun altitudes across the same sampling grid and derive twilight transitions.
            let sunAlts = times.map(time => {
                try { return this.calculator.calculateSunAltitude(time, latitude, longitude); }
                catch { return NaN; }
            });
            
            // Helper: find first crossing near a region (evening = left half, morning = right half)
            const midIndex = Math.floor(times.length / 2);
            const crossTime = (thr, evening=true) => {
                if (evening) {
                    // search descending (>thr to <=thr) from midIndex leftwards
                    for (let i = midIndex - 1; i >= 0; i--) {
                        const a1 = sunAlts[i];
                        const a2 = sunAlts[i+1];
                        if (isFinite(a1) && isFinite(a2) && a1 > thr && a2 <= thr) {
                            const frac = (thr - a1) / (a2 - a1);
                            const t = new Date(times[i].getTime() + frac * (times[i+1].getTime() - times[i].getTime()));
                            return t;
                        }
                    }
                    return null;
                } else {
                    // search ascending (<=thr to >thr) from midIndex rightwards
                    for (let i = midIndex; i < sunAlts.length - 1; i++) {
                        const a1 = sunAlts[i];
                        const a2 = sunAlts[i+1];
                        if (isFinite(a1) && isFinite(a2) && a1 <= thr && a2 > thr) {
                            const frac = (thr - a1) / (a2 - a1);
                            const t = new Date(times[i].getTime() + frac * (times[i+1].getTime() - times[i].getTime()));
                            return t;
                        }
                    }
                    return null;
                }
            };
            
            let twilightTimes = {
                civilEvening: crossTime(-6, true),
                nauticalEvening: crossTime(-12, true),
                astronomicalEvening: crossTime(-18, true),
                astronomicalMorning: crossTime(-18, false),
                nauticalMorning: crossTime(-12, false),
                civilMorning: crossTime(-6, false)
            };
            console.log('Derived twilight from sun altitudes:', twilightTimes);

            // Determine site timezone
            const siteTimezone = timezone && timezone !== 'auto' ? timezone : this.calculator.getTimezoneForLocation(latitude, longitude);

            // Helper: get IANA timezone offset (ms) for a given Date
            const getTzOffsetMs = (tz, date) => {
                const fmt = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                });
                const parts = fmt.formatToParts(date).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
                const y = parseInt(parts.year, 10);
                const m = parseInt(parts.month, 10);
                const d = parseInt(parts.day, 10);
                const H = parseInt(parts.hour, 10);
                const M = parseInt(parts.minute, 10);
                const S = parseInt(parts.second, 10);
                const utcTs = Date.UTC(y, m - 1, d, H, M, S);
                return utcTs - date.getTime();
            };

            // Rebuild the time grid anchored to site local noon so midnight is centered.
            const [yStr, mStr, dStr] = params.obs_date.split('-');
            const obsY = parseInt(yStr, 10);
            const obsM = parseInt(mStr, 10);
            const obsD = parseInt(dStr, 10);
            // find UTC timestamp that renders as site local 12:00:00
            const guessUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0);
            const guessDate = new Date(guessUtc);
            const offsetAtGuess = getTzOffsetMs(siteTimezone, guessDate);
            let siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - offsetAtGuess;
            // refine once in case DST causes offset change
            const refinedOffset = getTzOffsetMs(siteTimezone, new Date(siteNoonUtc));
            siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - refinedOffset;
            // Moon phase for the title, at local midnight (midpoint of this noon→noon window)
            const moonAtMidnightLabel = this.calculator.formatMoonPhase(
                this.calculator.calculateMoonIllumination(new Date(siteNoonUtc + 12 * 3600 * 1000))
            );

            // Re-generate times from site noon to next noon with the chosen interval
            const intervalMinutes = 2;
            const newTimes = [];
            const totalMinutes = 24 * 60;
            for (let i = 0; i <= totalMinutes; i += intervalMinutes) {
                newTimes.push(new Date(siteNoonUtc + i * 60 * 1000));
            }

            // Replace previously built arrays with site-anchored ones for plotting/labels
            // Keep original 'times' for calculations, since astronomy-engine uses absolute time
            // Recalculate object/sky values onto newTimes grid to ensure alignment
            const timesCalc = newTimes; // use the same for both plotting and calc so shapes align
            const displayShift = (t) => {
                const siteMs = getTzOffsetMs(siteTimezone, t);
                const browserMs = -t.getTimezoneOffset() * 60000;
                return new Date(t.getTime() + siteMs - browserMs);
            };
            const displayTimes = timesCalc.map(displayShift);

            // Recompute sun altitudes and twilight times on the site-anchored grid
            sunAlts = timesCalc.map(t => {
                try { return this.calculator.calculateSunAltitude(t, latitude, longitude, elevation); }
                catch { return NaN; }
            });
            (function recomputeTwilight() {
                const midIndex = Math.floor(timesCalc.length / 2);
                const crossTime2 = (thr, evening=true) => {
                    if (evening) {
                        for (let i = midIndex - 1; i >= 0; i--) {
                            const a1 = sunAlts[i], a2 = sunAlts[i+1];
                            if (isFinite(a1) && isFinite(a2) && a1 > thr && a2 <= thr) {
                                const frac = (thr - a1) / (a2 - a1);
                                return new Date(timesCalc[i].getTime() + frac * (timesCalc[i+1].getTime() - timesCalc[i].getTime()));
                            }
                        }
                        return null;
                    } else {
                        for (let i = midIndex; i < sunAlts.length - 1; i++) {
                            const a1 = sunAlts[i], a2 = sunAlts[i+1];
                            if (isFinite(a1) && isFinite(a2) && a1 <= thr && a2 > thr) {
                                const frac = (thr - a1) / (a2 - a1);
                                return new Date(timesCalc[i].getTime() + frac * (timesCalc[i+1].getTime() - timesCalc[i].getTime()));
                            }
                        }
                        return null;
                    }
                };
                twilightTimes = {
                    civilEvening: crossTime2(-6, true),
                    nauticalEvening: crossTime2(-12, true),
                    astronomicalEvening: crossTime2(-18, true),
                    astronomicalMorning: crossTime2(-18, false),
                    nauticalMorning: crossTime2(-12, false),
                    civilMorning: crossTime2(-6, false)
                };
            })();

            // Recompute object/moon/horizon series on site-anchored grid
            const objectAltsPlot = [];
            const moonAltsPlot = [];
            const horizonAltsPlot = [];
            const objectAzimuthsPlot = [];
            const moonSeparationsPlot = [];
            for (let i = 0; i < timesCalc.length; i++) {
                const t = timesCalc[i];
                const obj = planetName
                    ? this.calculator.calculatePlanetAltAz(t, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometAltAz(t, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.raDecToAltAz(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        latitude,
                        longitude,
                        t,
                        elevation
                    );
                objectAltsPlot.push(obj.altitude);
                objectAzimuthsPlot.push(obj.azimuth);
                let mAlt = null;
                try {
                    mAlt = this.calculator.calculateMoonAltitude(t, latitude, longitude, elevation);
                    if (mAlt === -90) mAlt = null;
                } catch {}
                moonAltsPlot.push(mAlt);

                // Calculate Moon separation for plot
                let moonSep = null;
                try {
                    moonSep = planetName
                        ? this.calculator.calculatePlanetMoonSeparation(t, latitude, longitude, elevation, planetName)
                        : cometDesignation
                        ? await this.calculator.calculateCometMoonSeparation(t, latitude, longitude, elevation, cometDesignation)
                        : this.calculator.calculateObjectMoonSeparation(
                            objectCoords.ra_hours,
                            objectCoords.dec_degrees,
                            t,
                            latitude,
                            longitude,
                            elevation
                        );
                } catch {}
                moonSeparationsPlot.push(moonSep);

                if (horizonProfile) {
                    horizonAltsPlot.push(this.calculator.interpolateHorizonAltitude(obj.azimuth, horizonProfile));
                }
            }

            // Create plot traces (matching Python implementation)
            // Use shifted Date objects so labels reflect observer local time
            const traces = [
                {
                    x: displayTimes,
                    y: objectAltsPlot,
                    mode: 'lines',
                    line: { color: 'blue', width: 3 },
                    hovertemplate: isMoon
                        ? 'Time: %{x|%H:%M}<br>Altitude: %{y:.2f}°<br>Azimuth: %{customdata[0]:.1f}°'
                        : 'Time: %{x|%H:%M}<br>Altitude: %{y:.2f}°<br>Azimuth: %{customdata[0]:.1f}°<br>Moon Sep: %{customdata[1]:.1f}°',
                    customdata: objectAzimuthsPlot.map((az, i) => [az, moonSeparationsPlot[i]]),
                    name: "Object Alt"
                },
                {
                    x: displayTimes,
                    y: moonAltsPlot,
                    mode: 'lines',
                    line: { color: '#555555', dash: 'dash', width: 3 },
                    hovertemplate: 'Time: %{x|%H:%M}<br>Moon Alt: %{y:.2f}°<br>Object Sep: %{customdata:.1f}°',
                    customdata: moonSeparationsPlot,
                    name: 'Moon',
                    visible: !isMoon,
                    connectgaps: false
                }
            ];

            if (horizonProfile) {
                traces.push({
                    x: displayTimes,
                    y: horizonAltsPlot,
                    mode: 'lines',
                    line: { color: 'brown', dash: 'dot', width: 1 },
                    hovertemplate: 'Time: %{x|%H:%M}<br>Horizon Alt: %{y:.2f}°',
                    name: 'Custom Horizon',
                    connectgaps: true
                });
            }

            traces.push({
                x: displayTimes,
                y: new Array(timesCalc.length).fill(0),
                mode: 'lines',
                line: { color: 'red', dash: 'dash', width: 1 },
                hoverinfo: 'skip',
                name: 'Horizon (0°)',
                showlegend: true
            });

            // Calculate dynamic range (include horizon only if provided)
            const allAlts = (horizonProfile ? [...objectAltsPlot, ...moonAltsPlot, ...horizonAltsPlot.filter(v => v != null)] : [...objectAltsPlot, ...moonAltsPlot]);
            const minAlt = Math.min(...allAlts);
            const maxAlt = Math.max(...allAlts);
            const altRange = maxAlt - minAlt;
            
            const yMin = Math.max(-20, minAlt - altRange * 0.1);
            const yMax = Math.min(90, maxAlt + altRange * 0.1);
            const onMobileAlt = (typeof window !== 'undefined') && window.innerWidth < 768;
            const baseHeightAlt = onMobileAlt ? 380 : 500;
            const plotHeight = Math.max(baseHeightAlt, Math.min(onMobileAlt ? 780 : 900, 360 + altRange * 3.5));

            // Create twilight shading shapes (daytime stays white)
            const shapes = [];
            // Debug logging removed
            
            if (twilightTimes) {
                const { civilEvening, nauticalEvening, astronomicalEvening, 
                        civilMorning, nauticalMorning, astronomicalMorning } = twilightTimes;
                
                // Detailed twilight logs removed
                
                // FIXED: Convert twilight times to proper format for plotting
                const formatTwilightTime = (time) => {
                    if (!time) return null;
                    
                    let dateTime;
                    if (time instanceof Date) {
                        dateTime = new Date(time);
                    } else if (typeof time === 'string') {
                        dateTime = new Date(time);
                    } else if (time.time) {
                        dateTime = new Date(time.time);
                    } else {
                        return null;
                    }
                    
                    // FIXED: Return the Date object directly instead of ISO string
                    // This maintains local time consistency with the plot data
                    return dateTime;
                };
                
                // Civil twilight (light gray)
                const civilEveningTime = formatTwilightTime(civilEvening);
                const nauticalEveningTime = formatTwilightTime(nauticalEvening);
                const civilMorningTime = formatTwilightTime(civilMorning);
                const nauticalMorningTime = formatTwilightTime(nauticalMorning);
                const astronomicalEveningTime = formatTwilightTime(astronomicalEvening);
                const astronomicalMorningTime = formatTwilightTime(astronomicalMorning);
                
                // Formatted twilight time logs removed
                
                // FIXED: Debug the actual time values
                // Raw twilight objects logging removed
                
                // Check if times are reasonable (should be in the 24-hour period)
                const plotStart = timesCalc[0];
                const plotEnd = timesCalc[timesCalc.length - 1];
                // Plot time range logs removed
                
                if (civilEveningTime && nauticalEveningTime) {
                    // FIXED: Check if twilight times are within plot range
                    const civilTime = new Date(civilEveningTime);
                    const nauticalTime = new Date(nauticalEveningTime);
                    
                    // Convert plot times to local time for comparison
                    const plotStartLocal = new Date(plotStart.getTime() + plotStart.getTimezoneOffset() * 60000);
                    const plotEndLocal = new Date(plotEnd.getTime() + plotEnd.getTimezoneOffset() * 60000);
                    
                    console.log('Time comparison:', {
                        civilTime: civilTime.toLocaleString(),
                        nauticalTime: nauticalTime.toLocaleString(),
                        plotStartLocal: plotStartLocal.toLocaleString(),
                        plotEndLocal: plotEndLocal.toLocaleString()
                    });
                    
                    if (civilTime >= plotStart && nauticalTime <= plotEnd) {
                        shapes.push({
                            type: 'rect',
                            xref: 'x',
                            yref: 'paper',
                            x0: displayShift(civilEveningTime), // shift to observer local time axis
                            x1: displayShift(nauticalEveningTime),
                            y0: 0,
                            y1: 1,
                            fillcolor: 'rgba(0,0,0,0.06)', // Light gray for civil twilight
                            line: { width: 0 },
                            layer: 'below'
                        });
                    }
                }
                if (nauticalMorningTime && civilMorningTime) {
                    // FIXED: Check if morning twilight times are within plot range
                    const nauticalMorning = new Date(nauticalMorningTime);
                    const civilMorning = new Date(civilMorningTime);
                    
                    // FIXED: Extend plot range to include morning twilight
                    // Morning twilight is on the next day, so we need to check if it's within 24 hours
                    const nextDayStart = new Date(plotStart.getTime() + 24 * 60 * 60 * 1000);
                    const nextDayEnd = new Date(plotEnd.getTime() + 24 * 60 * 60 * 1000);
                    
                    if ((nauticalMorning >= plotStart && civilMorning <= plotEnd) || 
                        (nauticalMorning >= nextDayStart && civilMorning <= nextDayEnd)) {
                        shapes.push({
                            type: 'rect',
                            xref: 'x',
                            yref: 'paper',
                            x0: displayShift(nauticalMorningTime),
                            x1: displayShift(civilMorningTime),
                            y0: 0,
                            y1: 1,
                            fillcolor: 'rgba(0,0,0,0.06)', // Light gray for civil twilight
                            line: { width: 0 },
                            layer: 'below'
                        });
                    }
                }
                
                // Nautical twilight (medium gray)
                if (nauticalEveningTime && astronomicalEveningTime) {
                    shapes.push({
                        type: 'rect',
                        xref: 'x',
                        yref: 'paper',
                        x0: displayShift(nauticalEveningTime),
                        x1: displayShift(astronomicalEveningTime),
                        y0: 0,
                        y1: 1,
                        fillcolor: 'rgba(0,0,0,0.12)', // Medium gray for nautical twilight
                        line: { width: 0 },
                        layer: 'below'
                    });
                }
                if (astronomicalMorningTime && nauticalMorningTime) {
                    shapes.push({
                        type: 'rect',
                        xref: 'x',
                        yref: 'paper',
                        x0: displayShift(astronomicalMorningTime),
                        x1: displayShift(nauticalMorningTime),
                        y0: 0,
                        y1: 1,
                        fillcolor: 'rgba(0,0,0,0.12)', // Medium gray for nautical twilight
                        line: { width: 0 },
                        layer: 'below'
                    });
                }
                
                // Astronomical night (darkest gray)
                if (astronomicalEveningTime && astronomicalMorningTime) {
                    shapes.push({
                        type: 'rect',
                        xref: 'x',
                        yref: 'paper',
                        x0: displayShift(astronomicalEveningTime),
                        x1: displayShift(astronomicalMorningTime),
                        y0: 0,
                        y1: 1,
                        fillcolor: 'rgba(0,0,0,0.20)', // Dark gray for astronomical night
                        line: { width: 0 },
                        layer: 'below'
                    });
                }
                
                // Shapes count log removed
                
                // FIXED: Add fallback twilight zones if some times are missing
                if (shapes.length === 0) {
                    // Fallback approach without debug noise
                    
                    // Try to create at least some twilight indication
                    // Use approximate times based on sun altitude
                    const sunAlts = timesCalc.map(time => {
                        try {
                            return this.calculator.calculateSunAltitude(time, latitude, longitude, elevation);
                        } catch (e) {
                            return 0;
                        }
                    });
                    
                    // Find when sun is at different altitudes
                    const civilTwilightAlt = -6;
                    const nauticalTwilightAlt = -12;
                    const astronomicalTwilightAlt = -18;
                    
                    // Find evening twilight times
                    let civilEveningFallback = null;
                    let nauticalEveningFallback = null;
                    let astronomicalEveningFallback = null;
                    
                    for (let i = 0; i < sunAlts.length; i++) {
                        if (sunAlts[i] <= civilTwilightAlt && !civilEveningFallback) {
                            civilEveningFallback = timesCalc[i];
                        }
                        if (sunAlts[i] <= nauticalTwilightAlt && !nauticalEveningFallback) {
                            nauticalEveningFallback = timesCalc[i];
                        }
                        if (sunAlts[i] <= astronomicalTwilightAlt && !astronomicalEveningFallback) {
                            astronomicalEveningFallback = timesCalc[i];
                        }
                    }
                    
                    // Create fallback shapes
                    if (civilEveningFallback && nauticalEveningFallback) {
                        shapes.push({
                            type: 'rect',
                            xref: 'x',
                            yref: 'paper',
                            x0: displayShift(civilEveningFallback),
                            x1: displayShift(nauticalEveningFallback),
                            y0: 0,
                            y1: 1,
                            fillcolor: 'rgba(0,0,0,0.06)', // Civil twilight fallback (light gray)
                            line: { width: 0 },
                            layer: 'below'
                        });
                    }
                    
                    // Fallback shapes count log removed
                }
            } else {
                // No twilight times calculated; apply a simple night band
                
                // Fallback: simple night centered on midnight (noon-to-noon range)
                const mid = new Date((timesCalc[0].getTime() + timesCalc[timesCalc.length - 1].getTime()) / 2);
                const nightStart = new Date(mid.getTime() - 4 * 60 * 60 * 1000); // 4 hours before midnight
                const nightEnd = new Date(mid.getTime() + 4 * 60 * 60 * 1000);   // 4 hours after midnight
                
                shapes.push({
                    type: 'rect',
                    xref: 'x',
                    yref: 'paper',
                    x0: displayShift(nightStart),
                    x1: displayShift(nightEnd),
                    y0: 0,
                    y1: 1,
                    fillcolor: 'rgba(0,0,0,0.20)', // Dark gray for night
                    line: { width: 0 },
                    layer: 'below'
                });
                // Fallback night period added
            }

            // Add a single vertical line marking midnight at center of the plot
            const startTime = displayTimes[0];
            const endTime = displayTimes[displayTimes.length - 1];
            const midnight = new Date((startTime.getTime() + endTime.getTime()) / 2);
            shapes.push({
                type: 'line',
                xref: 'x',
                yref: 'paper',
                x0: midnight,
                x1: midnight,
                y0: 0,
                y1: 1,
                line: { color: '#444', width: 2, dash: 'dot' }
            });
            
            // Time generation debug logs removed
            
            const layout = {
                xaxis: {
                    title: { text: `Time in Hours (${siteTimezone})`, font: { color: '#b0b0b0' } },
                    tickformat: "%H", // Show only hours without minutes
                    hoverformat: "%H:%M", // Show full time in hover tooltips
                    tickangle: 45, // Angle ticks at 45 degrees
                    dtick: onMobileAlt ? 7200000 : 3600000, // fewer ticks on mobile (2h)
                    type: 'date', // FIXED: Use date type for proper time handling
                    showgrid: true,
                    gridcolor: '#2a2a2a',
                    linecolor: '#2a2a2a',
                    tickfont: { color: '#b0b0b0' },
                    zeroline: false,
                    // FIXED: Explicitly set range to show full 24-hour period from midnight to midnight
                    range: [displayTimes[0], displayTimes[displayTimes.length - 1]], // Full range noon-to-noon (midnight centered)
                    tickmode: 'linear',
                    tick0: displayTimes[0], // Start ticks from noon
                    nticks: onMobileAlt ? 13 : 25 // hourly vs 2-hourly on mobile
                },
                yaxis: {
                    title: { text: 'Altitude (degrees)', font: { color: '#b0b0b0' } },
                    range: [Math.max(-20, yMin - 5), Math.min(90, yMax + 5)],
                    gridcolor: '#2a2a2a',
                    linecolor: '#2a2a2a',
                    tickfont: { color: '#b0b0b0' },
                    zeroline: false
                },
                title: {
                    text: `Altitude of ${params.object_name} (Local Time)<br>Location: ${formatLatitude(latitude, 1)}, ${formatLongitude(longitude, 1)}` +
                          `<br>Moon at local midnight: ${moonAtMidnightLabel}`,
                    font: { size: 16, color: '#e0e0e0' }
                },
                showlegend: true,
                legend: {
                    orientation: 'h',
                    x: 1,
                    xanchor: 'right',
                    y: 1,
                    yanchor: 'top',
                    bgcolor: 'rgba(10, 10, 10, 0.9)',
                    bordercolor: '#2a2a2a',
                    borderwidth: 1,
                    font: { size: 10, color: '#b0b0b0' }
                },
                margin: onMobileAlt ? { l: 48, r: 18, t: 96, b: 60 } : { l: 50, r: 50, t: 100, b: 50 },
                height: plotHeight,
                width: null,
                autosize: true,
                plot_bgcolor: '#141414',
                paper_bgcolor: '#0a0a0a',
                shapes: shapes,
                annotations: []
            };

            return {
                success: true,
                plot: {
                    data: traces,
                    layout: layout
                }
            };
        } catch (error) {
            console.error('Altitude plot error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Format time from timePoints array for display
     * @param {Array} timePoints - Array of Date objects
     * @param {number} index - Index in the array
     * @returns {string} Formatted time string
     */
    formatTimeFromArray(timePoints, index) {
        if (!timePoints || index >= timePoints.length) return '--:--';
        const time = timePoints[index];
        return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    /**
     * Create sky path (polar Alt–Az) plot for a specific date and location
     * @param {Object} params - Plot parameters (same as altitude plot)
     * @returns {Promise<Object>} Plotly polar plot data
     */
    async createSkyPathPlot(params) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            let objectCoords;
            const planetName = params.planet && params.planet.name ? params.planet.name : null;
            const isMoon = this.calculator.isMoonTarget({ kind: 'planet', planetName });
            const cometDesignation = params.comet && params.comet.designation ? params.comet.designation : null;

            // Check if manual coordinates are provided
            if (params.manual_coords) {
                objectCoords = {
                    ra_hours: params.manual_coords.ra_hours,
                    dec_degrees: params.manual_coords.dec_degrees,
                    name: params.manual_coords.name
                };
                console.log('Using manual coordinates for sky path plot:', objectCoords);
            } else if (planetName) {
                console.log('Using planet target for sky path plot:', planetName);
                objectCoords = null;
            } else if (cometDesignation) {
                console.log('Using comet target for sky path plot:', cometDesignation);
                objectCoords = null;
            } else {
                // Get object coordinates from database
                objectCoords = await this.getObjectCoordinates(params.object_name);
                if (!objectCoords) {
                    throw new Error(`Object '${params.object_name}' not found`);
                }
            }

            // Parse parameters
            const latitude = parseFloat(params.latitude);
            const longitude = parseFloat(params.longitude);
            let elevation = parseFloat(params.elevation);
            if (!isFinite(elevation)) elevation = 0;
            const timezone = params.timezone;
            const parsedObsDate = new Date(params.obs_date);
            // Validate inputs
            if (!isFinite(latitude) || latitude < -90 || latitude > 90) throw new Error('Latitude must be between -90 and 90.');
            if (!isFinite(longitude) || longitude < -180 || longitude > 180) throw new Error('Longitude must be between -180 and 180.');
            if (!(parsedObsDate instanceof Date) || isNaN(parsedObsDate.getTime())) throw new Error('Invalid observation date.');

            // Determine site timezone
            const siteTimezone = timezone && timezone !== 'auto' ? timezone : this.calculator.getTimezoneForLocation(latitude, longitude);

            // Helper to get zone offset in ms and shift for display
            const getTzOffsetMs = (tz, date) => {
                const dtf = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                });
                const parts = Object.fromEntries(dtf.formatToParts(date).map(p => [p.type, p.value]));
                const utcTs = Date.UTC(parseInt(parts.year,10), parseInt(parts.month,10)-1, parseInt(parts.day,10), parseInt(parts.hour,10), parseInt(parts.minute,10), parseInt(parts.second,10));
                return utcTs - date.getTime();
            };
            const displayShift = (t) => {
                const siteMs = getTzOffsetMs(siteTimezone, t);
                const browserMs = -t.getTimezoneOffset() * 60000;
                return new Date(t.getTime() + siteMs - browserMs);
            };

            // Anchor to site-local noon so midnight is centered
            const [yStr, mStr, dStr] = params.obs_date.split('-');
            const obsY = parseInt(yStr, 10);
            const obsM = parseInt(mStr, 10);
            const obsD = parseInt(dStr, 10);
            const guessUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0);
            const guessDate = new Date(guessUtc);
            const offsetAtGuess = getTzOffsetMs(siteTimezone, guessDate);
            let siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - offsetAtGuess;
            const refinedOffset = getTzOffsetMs(siteTimezone, new Date(siteNoonUtc));
            siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - refinedOffset;

            // Build UTC time grid and site-local display times
            const intervalMinutes = 2;
            const timesCalc = [];
            for (let i = 0; i <= 24*60; i += intervalMinutes) {
                timesCalc.push(new Date(siteNoonUtc + i * 60000));
            }
            const displayTimes = timesCalc.map(displayShift);
            const timeLabels = displayTimes.map(dt => dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

            // Parse optional custom horizon profile
            const parseHorizon = (text) => {
                if (!text || !text.trim()) return null;
                const trimmed = text.trim();
                if (trimmed.startsWith('[')) {
                    try {
                        const arr = JSON.parse(trimmed);
                        const out = [];
                        (arr || []).forEach(pair => {
                            if (Array.isArray(pair) && pair.length >= 2) {
                                const az = parseFloat(pair[0]);
                                const alt = parseFloat(pair[1]);
                                if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                            }
                        });
                        return out.length ? out : null;
                    } catch (_) {}
                }
                const entries = trimmed.split(/\n|;+/).map(s => s.trim()).filter(Boolean);
                const out = [];
                for (const e of entries) {
                    const parts = e.split(/[ ,\t]+/).filter(Boolean);
                    if (parts.length >= 2) {
                        const az = parseFloat(parts[0]);
                        const alt = parseFloat(parts[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                        continue;
                    }
                    const nums = e.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
                    if (nums && nums.length >= 2) {
                        const az = parseFloat(nums[0]);
                        const alt = parseFloat(nums[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                    }
                }
                return out.length ? out : null;
            };
            const horizonProfile = parseHorizon(params.horizon_data);

            // Compute object and moon Alt/Az
            const objAz = [];
            const objAlt = [];
            const moonAz = [];
            const moonAlt = [];
            const moonSeparations = [];
            for (const t of timesCalc) {
                const obj = planetName
                    ? this.calculator.calculatePlanetAltAz(t, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometAltAz(t, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.raDecToAltAz(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        latitude,
                        longitude,
                        t,
                        elevation
                    );
                objAz.push(obj.azimuth);
                objAlt.push(obj.altitude);
                const m = this.calculator.calculateMoonAltAz(t, latitude, longitude, elevation);
                moonAz.push(m.azimuth);
                moonAlt.push(m.altitude);

                // Calculate Moon separation
                const separation = planetName
                    ? this.calculator.calculatePlanetMoonSeparation(t, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometMoonSeparation(t, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.calculateObjectMoonSeparation(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        t,
                        latitude,
                        longitude,
                        elevation
                    );
                moonSeparations.push(isMoon ? null : separation);
            }

            // Convert to polar: theta=azimuth, r=90-altitude; mask below horizon or custom horizon profile
            const toPolar = (azArr, altArr) => {
                const theta = [];
                const r = [];
                for (let i = 0; i < altArr.length; i++) {
                    const alt = altArr[i];
                    const az = azArr[i];
                    if (!isFinite(alt)) { theta.push(null); r.push(null); continue; }

                    // Determine masking threshold: 0° horizon or custom horizon altitude if provided
                    let threshold = 0;
                    if (horizonProfile && Array.isArray(horizonProfile) && horizonProfile.length) {
                        try {
                            threshold = this.calculator.interpolateHorizonAltitude(az, horizonProfile);
                            if (!isFinite(threshold)) threshold = 0;
                        } catch (_) { threshold = 0; }
                    }

                    if (alt >= threshold) {
                        theta.push(az);
                        r.push(90 - alt);
                    } else { // below horizon or custom horizon
                        theta.push(null);
                        r.push(null);
                    }
                }
                return { theta, r };
            };
            
            const objPolar = toPolar(objAz, objAlt);
            const moonPolar = toPolar(moonAz, moonAlt);

            // Horizon outline
            let horizonTheta = null;
            let horizonR = null;
            if (horizonProfile) {
                const sorted = [...horizonProfile].sort((a, b) => a[0] - b[0]);
                const az = sorted.map(p => p[0]);
                const alt = sorted.map(p => p[1]);
                if (az[0] > 0) { az.unshift(0); alt.unshift(alt[0]); }
                if (az[az.length - 1] < 360) { az.push(360); alt.push(alt[alt.length - 1]); }
                horizonTheta = az;
                horizonR = alt.map(a => 90 - a);
            }

            // Build Plotly traces
            const traces = [
                {
                    type: 'scatterpolar',
                    theta: objPolar.theta,
                    r: objPolar.r,
                    mode: 'lines',
                    line: { color: 'blue', width: 2 },
                    name: `${params.object_name} Path`,
                    hoverinfo: 'skip'
                },
                {
                    type: 'scatterpolar',
                    theta: moonPolar.theta,
                    r: moonPolar.r,
                    mode: 'lines',
                    line: { color: 'silver', width: 2, dash: 'dot' },
                    name: 'Moon Path',
                    visible: !isMoon,
                    hoverinfo: 'skip'
                },
                // Current object position marker
                {
                    type: 'scatterpolar',
                    theta: [objPolar.theta[0]], 
                    r: [objPolar.r[0]],
                    mode: 'markers',
                    marker: { 
                        color: 'red', 
                        size: 12, 
                        symbol: 'circle',
                        line: { color: 'white', width: 2 }
                    },
                    name: `${params.object_name}`,
                    hovertemplate: `${params.object_name}<br>Time: %{text}<extra></extra>`,
                    text: [timeLabels[0]]
                },
                // Current moon position marker
                {
                    type: 'scatterpolar',
                    theta: [moonPolar.theta[0]], 
                    r: [moonPolar.r[0]],
                    mode: 'markers',
                    marker: { 
                        color: 'gold', 
                        size: 10, 
                        symbol: 'circle',
                        line: { color: 'darkgoldenrod', width: 1 }
                    },
                    name: 'Moon',
                    visible: !isMoon,
                    hovertemplate: `Moon<br>Time: %{text}<extra></extra>`,
                    text: [timeLabels[0]]
                }
            ];
            if (horizonTheta && horizonR) {
                traces.push({
                    type: 'scatterpolar',
                    theta: horizonTheta,
                    r: horizonR,
                    mode: 'lines',
                    line: { color: 'brown', width: 1, dash: 'dash' },
                    name: 'Custom Horizon',
                    hoverinfo: 'skip'
                });
            }

            const layout = {
                title: {
                    text: `Sky Path on ${params.obs_date} (Local)<br>` +
                          `Location: ${formatLatitude(latitude, 1)}, ${formatLongitude(longitude, 1)}`,
                    x: 0.5,
                    xanchor: 'center',
                    pad: { t: 8, b: 0 }
                },
                polar: {
                    bgcolor: '#141414',
                    radialaxis: {
                        range: [90, 0],
                        tickvals: [90, 75, 60, 45, 30, 15, 0],
                        ticktext: ['0°', '15°', '30°', '45°', '60°', '75°', '90°'],
                        gridcolor: '#2a2a2a',
                        linecolor: '#2a2a2a',
                        tickfont: { color: '#b0b0b0' }
                    },
                    angularaxis: {
                        rotation: 90,
                        direction: 'clockwise',
                        tickvals: [0, 90, 180, 270],
                        ticktext: ['N', 'E', 'S', 'W'],
                        tickfont: { size: 12, color: '#ffffff' },
                        gridcolor: '#2a2a2a',
                        linecolor: '#2a2a2a'
                    }
                },
                showlegend: true,
                legend: {
                    orientation: 'h',
                    x: 1,
                    xanchor: 'right',
                    y: 1,
                    yanchor: 'top',
                    bgcolor: 'rgba(10, 10, 10, 0.9)',
                    bordercolor: '#2a2a2a',
                    borderwidth: 1,
                    font: { size: 10, color: '#b0b0b0' }
                },
                hovermode: false,
                height: 460,
                margin: { l: 40, r: 40, t: 96, b: 42 }
            };

            // Store time data for animation
            const plotData = {
                data: traces,
                layout,
                timePoints: timesCalc,
                displayTimes,
                timeLabels,
                objPolar,
                moonPolar,
                moonSeparations,
                isMoonTarget: isMoon,
                objectName: params.object_name
            };

            return { success: true, plot: plotData };
        } catch (error) {
            console.error('Sky path plot error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Create sky sphere (3D) plot for a specific date and location
     * @param {Object} params - Plot parameters
     * @returns {Promise<Object>} Plotly 3D plot data
     */
    async createSkySpherePlot(params) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        try {
            let objectCoords;
            const planetName = params.planet && params.planet.name ? params.planet.name : null;
            const isMoon = this.calculator.isMoonTarget({ kind: 'planet', planetName });
            const cometDesignation = params.comet && params.comet.designation ? params.comet.designation : null;

            // Check if manual coordinates are provided
            if (params.manual_coords) {
                objectCoords = {
                    ra_hours: params.manual_coords.ra_hours,
                    dec_degrees: params.manual_coords.dec_degrees,
                    name: params.manual_coords.name
                };
                console.log('Using manual coordinates for sky sphere plot:', objectCoords);
            } else if (planetName) {
                console.log('Using planet target for sky sphere plot:', planetName);
                objectCoords = null;
            } else if (cometDesignation) {
                console.log('Using comet target for sky sphere plot:', cometDesignation);
                objectCoords = null;
            } else {
                // Get object coordinates from database
                objectCoords = await this.getObjectCoordinates(params.object_name);
                if (!objectCoords) throw new Error(`Object '${params.object_name}' not found`);
            }

            const latitude = parseFloat(params.latitude);
            const longitude = parseFloat(params.longitude);
            let elevation = parseFloat(params.elevation);
            if (!isFinite(elevation)) elevation = 0;
            const timezone = params.timezone;
            const parsedObsDate = new Date(params.obs_date);
            // Validate inputs
            if (!isFinite(latitude) || latitude < -90 || latitude > 90) throw new Error('Latitude must be between -90 and 90.');
            if (!isFinite(longitude) || longitude < -180 || longitude > 180) throw new Error('Longitude must be between -180 and 180.');
            if (!(parsedObsDate instanceof Date) || isNaN(parsedObsDate.getTime())) throw new Error('Invalid observation date.');

            // Determine site timezone
            const siteTimezone = timezone && timezone !== 'auto' ? timezone : this.calculator.getTimezoneForLocation(latitude, longitude);

            // Helper to get zone offset and display shift
            const getTzOffsetMs = (tz, date) => {
                const dtf = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz,
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
                });
                const parts = Object.fromEntries(dtf.formatToParts(date).map(p => [p.type, p.value]));
                const utcTs = Date.UTC(parseInt(parts.year,10), parseInt(parts.month,10)-1, parseInt(parts.day,10), parseInt(parts.hour,10), parseInt(parts.minute,10), parseInt(parts.second,10));
                return utcTs - date.getTime();
            };
            const displayShift = (t) => {
                const siteMs = getTzOffsetMs(siteTimezone, t);
                const browserMs = -t.getTimezoneOffset() * 60000;
                return new Date(t.getTime() + siteMs - browserMs);
            };

            // Anchor to site-local noon
            const [yStr, mStr, dStr] = params.obs_date.split('-');
            const obsY = parseInt(yStr, 10);
            const obsM = parseInt(mStr, 10);
            const obsD = parseInt(dStr, 10);
            const guessUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0);
            const guessDate = new Date(guessUtc);
            const offsetAtGuess = getTzOffsetMs(siteTimezone, guessDate);
            let siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - offsetAtGuess;
            const refinedOffset = getTzOffsetMs(siteTimezone, new Date(siteNoonUtc));
            siteNoonUtc = Date.UTC(obsY, obsM - 1, obsD, 12, 0, 0) - refinedOffset;

            // Build UTC time grid and display times
            const intervalMinutes = 2;
            const timePoints = [];
            for (let i = 0; i <= 24*60; i += intervalMinutes) timePoints.push(new Date(siteNoonUtc + i * 60000));
            const displayTimes = timePoints.map(displayShift);
            const timeLabels = displayTimes.map(dt => dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));

            // Parse optional custom horizon profile
            const parseHorizon = (text) => {
                if (!text || !text.trim()) return null;
                const trimmed = text.trim();
                if (trimmed.startsWith('[')) {
                    try {
                        const arr = JSON.parse(trimmed);
                        const out = [];
                        (arr || []).forEach(pair => {
                            if (Array.isArray(pair) && pair.length >= 2) {
                                const az = parseFloat(pair[0]);
                                const alt = parseFloat(pair[1]);
                                if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                            }
                        });
                        return out.length ? out : null;
                    } catch (_) {}
                }
                const entries = trimmed.split(/\n|;+/).map(s => s.trim()).filter(Boolean);
                const out = [];
                for (const e of entries) {
                    const parts = e.split(/[ ,\t]+/).filter(Boolean);
                    if (parts.length >= 2) {
                        const az = parseFloat(parts[0]);
                        const alt = parseFloat(parts[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                        continue;
                    }
                    const nums = e.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
                    if (nums && nums.length >= 2) {
                        const az = parseFloat(nums[0]);
                        const alt = parseFloat(nums[1]);
                        if (isFinite(az) && isFinite(alt)) out.push([az, alt]);
                    }
                }
                return out.length ? out : null;
            };
            const horizonProfile = parseHorizon(params.horizon_data);

            // Compute Alt/Az arrays
            const objAz = [];
            const objAlt = [];
            const moonAz = [];
            const moonAlt = [];
            const moonSeparations = [];
            for (const t of timePoints) {
                const obj = planetName
                    ? this.calculator.calculatePlanetAltAz(t, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometAltAz(t, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.raDecToAltAz(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        latitude,
                        longitude,
                        t,
                        elevation
                    );
                objAz.push(obj.azimuth);
                objAlt.push(obj.altitude);
                const m = this.calculator.calculateMoonAltAz(t, latitude, longitude, elevation);
                moonAz.push(m.azimuth);
                moonAlt.push(m.altitude);

                // Calculate Moon separation
                const separation = planetName
                    ? this.calculator.calculatePlanetMoonSeparation(t, latitude, longitude, elevation, planetName)
                    : cometDesignation
                    ? await this.calculator.calculateCometMoonSeparation(t, latitude, longitude, elevation, cometDesignation)
                    : this.calculator.calculateObjectMoonSeparation(
                        objectCoords.ra_hours,
                        objectCoords.dec_degrees,
                        t,
                        latitude,
                        longitude,
                        elevation
                    );
                moonSeparations.push(isMoon ? null : separation);
            }

            // Convert Alt/Az to ENU unit sphere coordinates
            const toENU = (azDeg, altDeg) => {
                const az = (azDeg * Math.PI) / 180.0;
                const alt = (altDeg * Math.PI) / 180.0;
                const x = Math.cos(alt) * Math.sin(az); // East
                const y = Math.cos(alt) * Math.cos(az); // North
                const z = Math.sin(alt);                // Up
                return { x, y, z };
            };

            const objX = [], objY = [], objZ = [];
            const moonX = [], moonY = [], moonZ = [];
            for (let i = 0; i < objAz.length; i++) {
                if (isFinite(objAlt[i]) && objAlt[i] >= 0) {
                    const v = toENU(objAz[i], objAlt[i]);
                objX.push(v.x); objY.push(v.y); objZ.push(v.z);
                } else {
                    objX.push(null); objY.push(null); objZ.push(null);
                }
                if (isFinite(moonAlt[i]) && moonAlt[i] >= 0) {
                    const m = toENU(moonAz[i], moonAlt[i]);
                moonX.push(m.x); moonY.push(m.y); moonZ.push(m.z);
                } else {
                    moonX.push(null); moonY.push(null); moonZ.push(null);
                }
            }

            // Choose the first above-horizon sample for initial markers
            const firstObjIdx = (() => {
                for (let i = 0; i < objX.length; i++) {
                    if (objX[i] !== null && objY[i] !== null && objZ[i] !== null) return i;
                }
                return 0;
            })();
            const firstMoonIdx = (() => {
                for (let i = 0; i < moonX.length; i++) {
                    if (moonX[i] !== null && moonY[i] !== null && moonZ[i] !== null) return i;
                }
                return 0;
            })();

            // Optional: custom horizon polyline in 3D
            let hzX = null, hzY = null, hzZ = null;
            if (horizonProfile) {
                const sorted = [...horizonProfile].sort((a, b) => a[0] - b[0]);
                const az = sorted.map(p => p[0]);
                const alt = sorted.map(p => p[1]);
                if (az[0] > 0) { az.unshift(0); alt.unshift(alt[0]); }
                if (az[az.length - 1] < 360) { az.push(360); alt.push(alt[alt.length - 1]); }
                hzX = []; hzY = []; hzZ = [];
                for (let i = 0; i < az.length; i++) {
                    const v = toENU(az[i], alt[i]);
                    hzX.push(v.x); hzY.push(v.y); hzZ.push(v.z);
                }
            }

            // Unit sphere surface (light, semi-transparent)
            const stepsU = 36, stepsV = 10; // denser around horizon, half-sphere only
            const uVals = Array.from({length: stepsU}, (_, i) => (2*Math.PI*i)/(stepsU-1));
            const vVals = Array.from({length: stepsV}, (_, j) => ((Math.PI/2)*j)/(stepsV-1)); // 0..pi/2 (above horizon)
            const sphereX = [], sphereY = [], sphereZ = [];
            for (let j = 0; j < vVals.length; j++) {
                const rowX = [], rowY = [], rowZ = [];
                const v = vVals[j];
                const sv = Math.sin(v), cv = Math.cos(v);
                for (let i = 0; i < uVals.length; i++) {
                    const u = uVals[i];
                    const cu = Math.cos(u), su = Math.sin(u);
                    // Fix ENU coordinates: X=East(sin), Y=North(cos), Z=Up
                    rowX.push(su * sv);  // East = sin(azimuth) * sin(elevation)
                    rowY.push(cu * sv);  // North = cos(azimuth) * sin(elevation) 
                    rowZ.push(cv);       // Up = cos(elevation)
                }
                sphereX.push(rowX); sphereY.push(rowY); sphereZ.push(rowZ);
            }

            const traces = [
                {
                    type: 'surface',
                    x: sphereX,
                    y: sphereY,
                    z: sphereZ,
                    opacity: 0.18,
                    showscale: false,
                    colorscale: [[0, '#cce7ff'], [1, '#cce7ff']],
                    hoverinfo: 'skip'
                },
                {
                    type: 'scatter3d',
                    x: objX, y: objY, z: objZ,
                    mode: 'lines',
                    line: { color: 'blue', width: 5 },
                    name: `${params.object_name} Path`,
                    hoverinfo: 'skip'
                },
                {
                    type: 'scatter3d',
                    x: moonX, y: moonY, z: moonZ,
                    mode: 'lines',
                    line: { color: 'silver', width: 5, dash: 'dash' },
                    name: 'Moon Path',
                    visible: !isMoon,
                    hoverinfo: 'skip'
                },
                // Current object position marker
                {
                    type: 'scatter3d',
                    x: [objX[firstObjIdx]], y: [objY[firstObjIdx]], z: [objZ[firstObjIdx]],
                    mode: 'markers',
                    marker: { 
                        color: 'red', 
                        size: 8,
                        symbol: 'circle',
                        line: { color: 'white', width: 2 }
                    },
                    name: `${params.object_name}`,
                    hovertemplate: `${params.object_name}<br>Time: %{text}<extra></extra>`,
                    text: [this.formatTimeFromArray(timePoints, firstObjIdx)]
                },
                // Current moon position marker  
                {
                    type: 'scatter3d',
                    x: [moonX[firstMoonIdx]], y: [moonY[firstMoonIdx]], z: [moonZ[firstMoonIdx]],
                    mode: 'markers',
                    marker: { 
                        color: 'gold', 
                        size: 6,
                        symbol: 'circle',
                        line: { color: 'darkgoldenrod', width: 1 }
                    },
                    name: 'Moon',
                    visible: !isMoon,
                    hovertemplate: `Moon<br>Time: %{text}<extra></extra>`,
                    text: [this.formatTimeFromArray(timePoints, firstMoonIdx)]
                },
                // Cardinal points on horizon (z=0 circle)
                {
                    type: 'scatter3d',
                    x: [0, 1, 0, -1],
                    y: [1, 0, -1, 0],
                    z: [0, 0, 0, 0],
                    mode: 'text',
                    text: ['N', 'E', 'S', 'W'],
                    textposition: 'middle center',
                    textfont: { size: 14, color: '#ffffff' },
                    hoverinfo: 'skip',
                    showlegend: false
                }
            ];

            if (hzX) {
                traces.push({
                    type: 'scatter3d',
                    x: hzX, y: hzY, z: hzZ,
                    mode: 'lines',
                    line: { color: 'brown', width: 2, dash: 'dash' },
                    name: 'Custom Horizon'
                });
            }

            const axisConfig = {
                visible: false,
                showaxeslabels: false,
                showspikes: false,
                showgrid: false,
                showline: false,
                zeroline: false,
                showbackground: false,
                showticklabels: false,
                ticks: '',
                tickwidth: 0,
                ticklen: 0,
                tickvals: [],
                ticktext: [],
                range: [-1.05, 1.05]
            };

            const layout = {
                title: {
                    text: `Sky Sphere on ${params.obs_date} (Local)<br>` +
                          `Location: ${formatLatitude(latitude, 1)}, ${formatLongitude(longitude, 1)}`,
                    x: 0.5,
                    xanchor: 'center',
                    pad: { t: 8, b: 0 }
                },
                scene: {
                    domain: { x: [0, 1], y: [0, 1] },
                    xaxis: { ...axisConfig },
                    yaxis: { ...axisConfig },
                    zaxis: { ...axisConfig },
                    // Use 'cube' to force equal axis scaling regardless of plot size
                    aspectmode: 'cube',
                    // Keep for explicitness; Plotly will balance with 'cube'
                    aspectratio: { x: 1, y: 1, z: 1 },
                    camera: { projection: { type: 'orthographic' }, center: { x: 0, y: 0, z: 0 } }
                },
                showlegend: true,
                legend: {
                    orientation: 'h',
                    x: 0.98,
                    xanchor: 'right',
                    y: 0.98,
                    yanchor: 'top',
                    bgcolor: 'rgba(10, 10, 10, 0.9)',
                    bordercolor: '#2a2a2a',
                    borderwidth: 1,
                    font: { size: 10, color: '#b0b0b0' }
                },
                hovermode: false,
                height: 620,
                paper_bgcolor: '#0a0a0a',
                plot_bgcolor: '#141414',
                // Align top spacing with other plots for a consistent title position
                margin: { l: 10, r: 10, t: 80, b: 10 }
            };

            // Store time data for animation
            const plotData = {
                data: traces,
                layout,
                timePoints,
                displayTimes,
                timeLabels,
                objCoords: { x: objX, y: objY, z: objZ },
                moonCoords: { x: moonX, y: moonY, z: moonZ },
                moonSeparations,
                isMoonTarget: isMoon,
                objectName: params.object_name
            };

            return { success: true, plot: plotData };
        } catch (error) {
            console.error('Sky sphere plot error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Generate time points for a day (noon-to-noon so midnight is centered)
     * @param {Date} date - Date
     * @param {number} intervalMinutes - Interval in minutes
     * @returns {Array} Array of Date objects
     */
    generateTimePoints(date, intervalMinutes) {
        const points = [];
        // Start from local noon and go to next day's noon, so midnight falls at center
        const startTime = new Date(date);
        startTime.setHours(12, 0, 0, 0); // Start at local noon
        
        // Generate exactly 24 hours of points (noon to noon)
        // Ensure we get exactly (24 * 60 / intervalMinutes) points
        const totalMinutes = 24 * 60;
        const numPoints = Math.floor(totalMinutes / intervalMinutes);
        
        for (let i = 0; i < numPoints; i++) {
            const time = new Date(startTime);
            time.setMinutes(startTime.getMinutes() + (i * intervalMinutes));
            points.push(time);
        }
        
        // Add the final point (next midnight) to complete the 24-hour cycle
        const endTime = new Date(startTime);
        endTime.setMinutes(startTime.getMinutes() + totalMinutes);
        points.push(endTime);
        
        return points;
    }

    /**
     * Search for astronomical objects
     * @param {string} query - Search query
     * @param {number} maxResults - Maximum results
     * @returns {Promise<Array>} Search results
     */
    async searchObjects(query, maxResults = 15) {
        if (!this.isInitialized) {
            await this.initialize();
        }

        // Get local results first
        const localResults = await this.searchEngine.searchObjects(query, maxResults);
        
        // If we have fewer than maxResults/2, try to supplement with SIMBAD
        if (localResults.length < maxResults / 2) {
            try {
                console.log(`Supplementing search with SIMBAD results for: ${query}`);
                const simbadResults = await this.simbadClient.searchObjects(query, maxResults - localResults.length);
                
                // Add SIMBAD results with source indicator
                const enhancedSimbadResults = simbadResults.map(obj => ({
                    ...obj,
                    catalog: 'SIMBAD',
                    description: `${obj.name} (from SIMBAD)`,
                    source: 'SIMBAD'
                }));
                
                return [...localResults, ...enhancedSimbadResults].slice(0, maxResults);
            } catch (error) {
                console.error('SIMBAD search supplement failed:', error);
            }
        }
        
        return localResults;
    }

    /**
     * Get application statistics
     * @returns {Promise<Object>} Application statistics
     */
    async getStats() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        const searchStats = await this.searchEngine.getStats();
        const cacheStats = this.searchEngine.getCacheStats();

        return {
            ...searchStats,
            cache: cacheStats,
            initialized: this.isInitialized
        };
    }

    /**
     * Display results in the UI
     * @param {Object} data - Results data
     */
    displayResults(data) {
        if (data.success) {
            // Show results section first so container has proper dimensions
            document.getElementById('resultsSection').style.display = 'block';
            
            // Small delay to ensure DOM is rendered
            setTimeout(() => {
                // Display plot
                this.plotlyManager.displayPlot('qualityPlot', data.plot);
                
                // Force resize after plot creation to ensure full width
                setTimeout(() => {
                    this.plotlyManager.resizeAllPlots();
                }, 200);
            }, 50);
            
            // Display summary
            this.displaySummary(data.summary);
        } else {
            this.displayError(data.error);
        }
    }

    /**
     * Display summary statistics
     * @param {Object} summary - Summary statistics
     */
    displaySummary(summary) {
        const summaryCards = document.getElementById('summaryCards');
        summaryCards.innerHTML = `
            <div class="col-md-4">
                <div class="summary-card">
                    <h5><i class="fas fa-clock"></i> Average Hours</h5>
                    <h3>${summary.avg_hours.toFixed(2)}</h3>
                </div>
            </div>
            <div class="col-md-4">
                <div class="summary-card">
                    <h5><i class="fas fa-arrow-up"></i> Maximum Hours</h5>
                    <h3>${summary.max_hours.toFixed(2)}</h3>
                </div>
            </div>
            <div class="col-md-4">
                <div class="summary-card">
                    <h5><i class="fas fa-arrow-down"></i> Minimum Hours</h3>
                    <h3>${summary.min_hours.toFixed(2)}</h3>
                </div>
            </div>
        `;
    }

    /**
     * Display error message
     * @param {string} message - Error message
     */
    displayError(message) {
        const errorAlert = document.getElementById('errorAlert');
        const errorMessage = document.getElementById('errorMessage');
        
        errorMessage.textContent = message;
        errorAlert.style.display = 'block';
        
        // Hide loading
        document.getElementById('loading').style.display = 'none';
    }
}

export default AstroImagingApp;
